import { useEffect, useState } from "react";
import { Sun, CloudSun, Cloud, CloudFog, CloudDrizzle, CloudRain, Snowflake, CloudLightning } from "lucide-react";
import { useLang } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONDITIONS = [
  { codes: [0], en: "Clear sky", de: "Klarer Himmel", Icon: Sun },
  { codes: [1, 2], en: "Partly cloudy", de: "Teils bewölkt", Icon: CloudSun },
  { codes: [3], en: "Overcast", de: "Bedeckt", Icon: Cloud },
  { codes: [45, 48], en: "Fog", de: "Nebel", Icon: CloudFog },
  { codes: [51, 53, 55, 56, 57], en: "Drizzle", de: "Nieselregen", Icon: CloudDrizzle },
  { codes: [61, 63, 65, 66, 67, 80, 81, 82], en: "Rain", de: "Regen", Icon: CloudRain },
  { codes: [71, 73, 75, 77, 85, 86], en: "Snow", de: "Schnee", Icon: Snowflake },
  { codes: [95, 96, 99], en: "Thunderstorm", de: "Gewitter", Icon: CloudLightning },
];

const getCondition = (code) =>
  CONDITIONS.find((c) => c.codes.includes(code)) || { en: "Changeable", de: "Wechselhaft", Icon: CloudSun };

const useWeather = (place) => {
  const [weather, setWeather] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await fetch(`${API}/weather/${place}`);
        if (!res.ok) return;
        const data = await res.json();
        if (alive) setWeather(data);
      } catch {
        /* badge simply stays hidden */
      }
    };
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(id); };
  }, [place]);
  return weather;
};

export default function WeatherBadge() {
  const weather = useWeather("salzburg");
  const { lang } = useLang();

  if (!weather) return null;
  const { Icon, en, de } = getCondition(weather.weather_code);

  return (
    <div data-testid="weather-badge" className="flex items-center gap-2.5 text-sm text-stone-300">
      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-white/5 border border-[#C5A059]/30">
        <Icon className="w-4 h-4 text-[#C5A059]" />
      </span>
      Salzburg · {weather.temperature_c}°C · {lang === "de" ? de : en}
    </div>
  );
}

export const WeatherChip = ({ place }) => {
  const weather = useWeather(place);
  if (!weather) return null;
  const { Icon } = getCondition(weather.weather_code);

  return (
    <span
      data-testid={`weather-chip-${place}`}
      className="flex items-center gap-1.5 rounded-full bg-black/60 backdrop-blur px-3.5 py-1.5 text-xs font-semibold text-white border border-white/20"
    >
      <Icon className="w-3.5 h-3.5 text-[#C5A059]" />
      {weather.temperature_c}°C
    </span>
  );
};
